import { useEffect, useState } from "react";
import { useAdminAuth } from "./AdminAuthContext";
import MediaUpload from "./MediaUpload";
import { toast, Toaster } from "sonner";
import { Save, Plus, Trash2, Edit3, X, Check, Mail, Phone } from "lucide-react";

const cls = {
  card: "border border-white/10 bg-[#121217] p-6",
  h1: "font-heading text-3xl font-bold mb-2",
  sub: "text-white/60 mb-8 text-sm",
  input: "w-full bg-white/5 border border-white/15 px-3 py-2 text-white text-sm outline-none focus:border-[#ff9c00]",
  label: "text-xs uppercase tracking-wider text-white/50 block mb-1",
  btn: "px-4 py-2 bg-[#ff9c00] text-black font-bold text-sm hover:bg-[#ffb133] inline-flex items-center gap-2 disabled:opacity-50",
  btnGhost: "px-4 py-2 border border-white/15 text-white/80 text-sm hover:border-white inline-flex items-center gap-2",
  btnDanger: "px-3 py-2 border border-red-400/40 text-red-300 text-sm hover:bg-red-400/10 inline-flex items-center gap-2",
};

// ============== DASHBOARD ==============
export function AdminDashboard() {
  const { adminFetch, user } = useAdminAuth();
  const [stats, setStats] = useState({ machines: 0, blog: 0, cases: 0, contacts: 0 });
  useEffect(() => {
    Promise.all([
      adminFetch("get", "/admin/machines"),
      adminFetch("get", "/admin/blog"),
      adminFetch("get", "/admin/cases"),
      adminFetch("get", "/admin/contacts"),
    ]).then(([m, b, c, k]) => setStats({
      machines: m.machines.length, blog: b.articles.length,
      cases: c.cases.length, contacts: k.submissions.length,
    })).catch(() => {});
  }, []);
  const cards = [
    { l: "Equipos dermo faciales", v: stats.machines, to: "/admin/equipos" },
    { l: "Artículos blog", v: stats.blog, to: "/admin/blog" },
    { l: "Casos de éxito", v: stats.cases, to: "/admin/casos" },
    { l: "Mensajes recibidos", v: stats.contacts, to: "/admin/contactos" },
  ];
  return (
    <div data-testid="admin-dashboard">
      <h1 className={cls.h1}>Bienvenido, {user?.email?.split("@")[0]}</h1>
      <p className={cls.sub}>Panel de gestión de contenidos · Asirox</p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <a key={c.l} href={c.to} className={`${cls.card} hover:border-[#ff9c00] transition`}>
            <p className="text-xs uppercase tracking-wider text-white/50">{c.l}</p>
            <p className="font-heading text-5xl font-extrabold text-[#ff9c00] mt-3">{c.v}</p>
          </a>
        ))}
      </div>
    </div>
  );
}

// ============== EQUIPOS ==============
export function AdminEquipos() {
  const { adminFetch } = useAdminAuth();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => adminFetch("get", "/admin/machines").then((d) => setList(d.machines));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminFetch("put", `/admin/machines/${editing.slug}`, editing);
      toast.success("Equipo actualizado");
      setEditing(null);
      load();
    } catch (e2) { toast.error("Error al guardar"); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div data-testid="admin-equipos-edit">
        <Toaster theme="dark"/>
        <button onClick={() => setEditing(null)} className={`${cls.btnGhost} mb-6`}><X size={14}/> Volver</button>
        <h1 className={cls.h1}>Editar · {editing.name}</h1>
        <form onSubmit={save} className={`${cls.card} space-y-4 max-w-3xl`}>
          {[
            ["name", "Nombre"], ["category", "Categoría"], ["tagline", "Tagline"],
            ["wavelength", "Longitud de onda / Tecnología"], ["power", "Potencia"],
            ["spot_size", "Spot / Pieza de mano"], ["frequency", "Frecuencia"],
            ["cooling", "Refrigeración"],
          ].map(([k, l]) => (
            <div key={k}>
              <label className={cls.label}>{l}</label>
              <input className={cls.input} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}/>
            </div>
          ))}
          <MediaUpload label="Imagen principal" value={editing.hero_image} onChange={(v) => setEditing({ ...editing, hero_image: v })} accept="image/*" testid="equipo-hero"/>
          <MediaUpload label="Video (mp4/webm)" value={editing.video_url} onChange={(v) => setEditing({ ...editing, video_url: v })} accept="video/*" testid="equipo-video"/>
          <div>
            <label className={cls.label}>Descripción corta</label>
            <textarea rows={3} className={cls.input} value={editing.short_description || ""} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })}/>
          </div>
          <div>
            <label className={cls.label}>Descripción larga</label>
            <textarea rows={8} className={cls.input} value={editing.long_description || ""} onChange={(e) => setEditing({ ...editing, long_description: e.target.value })}/>
          </div>
          <ListEditor label="Aplicaciones" value={editing.applications} onChange={(v) => setEditing({ ...editing, applications: v })} testid="applications"/>
          <ListEditor label="Beneficios" value={editing.benefits} onChange={(v) => setEditing({ ...editing, benefits: v })} testid="benefits"/>
          <button type="submit" disabled={busy} className={cls.btn} data-testid="admin-equipo-save"><Save size={14}/> {busy ? "Guardando..." : "Guardar cambios"}</button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-equipos">
      <Toaster theme="dark"/>
      <h1 className={cls.h1}>Equipos dermo faciales</h1>
      <p className={cls.sub}>Edita los textos, specs y aplicaciones de cada equipo.</p>
      <div className="space-y-3">
        {list.map((m) => (
          <div key={m.slug} className={`${cls.card} flex items-center justify-between`}>
            <div>
              <p className="font-heading text-lg font-bold text-white">{m.name}</p>
              <p className="text-xs text-white/50">{m.category}</p>
            </div>
            <button onClick={() => setEditing({ ...m })} className={cls.btn} data-testid={`admin-edit-${m.slug}`}><Edit3 size={14}/> Editar</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ListEditor({ label, value = [], onChange, testid }) {
  return (
    <div>
      <label className={cls.label}>{label}</label>
      {(value || []).map((v, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <input className={cls.input} value={v} onChange={(e) => { const a = [...value]; a[i] = e.target.value; onChange(a); }}/>
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className={cls.btnDanger}><Trash2 size={12}/></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...(value || []), ""])} className={cls.btnGhost} data-testid={`admin-add-${testid}`}><Plus size={12}/> Añadir</button>
    </div>
  );
}

// ============== BLOG ==============
export function AdminBlog() {
  const { adminFetch } = useAdminAuth();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => adminFetch("get", "/admin/blog").then((d) => setList(d.articles));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const isNew = !editing._origSlug;
      const slug = (editing.slug || "").trim();
      if (isNew) await adminFetch("post", "/admin/blog", editing);
      else await adminFetch("put", `/admin/blog/${editing._origSlug}`, editing);
      toast.success(isNew ? "Artículo creado" : "Artículo actualizado");
      setEditing(null); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Error al guardar"); }
    finally { setBusy(false); }
  };

  const remove = async (slug) => {
    if (!confirm(`¿Borrar artículo "${slug}"?`)) return;
    await adminFetch("delete", `/admin/blog/${slug}`);
    toast.success("Borrado"); load();
  };

  if (editing) {
    return (
      <div data-testid="admin-blog-edit">
        <Toaster theme="dark"/>
        <button onClick={() => setEditing(null)} className={`${cls.btnGhost} mb-6`}><X size={14}/> Volver</button>
        <h1 className={cls.h1}>{editing._origSlug ? "Editar" : "Nuevo"} artículo</h1>
        <form onSubmit={save} className={`${cls.card} space-y-4 max-w-4xl`}>
          {[["slug", "Slug URL (kebab-case)"], ["title", "Título"], ["category", "Categoría"], ["read_time", "Lectura (ej: 8 min)"]].map(([k, l]) => (
            <div key={k}>
              <label className={cls.label}>{l}</label>
              <input className={cls.input} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}/>
            </div>
          ))}
          <MediaUpload label="Imagen principal" value={editing.hero_image} onChange={(v) => setEditing({ ...editing, hero_image: v })} accept="image/*" testid="blog-hero"/>
          <div>
            <label className={cls.label}>Resumen / Excerpt</label>
            <textarea rows={3} className={cls.input} value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}/>
          </div>
          <div>
            <label className={cls.label}>Tags (separados por coma)</label>
            <input className={cls.input} value={(editing.tags || []).join(", ")} onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}/>
          </div>
          <div>
            <label className={cls.label}>Contenido HTML (usa &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;)</label>
            <textarea rows={18} className={`${cls.input} font-mono text-xs`} value={editing.content || ""} onChange={(e) => setEditing({ ...editing, content: e.target.value })}/>
          </div>
          <button type="submit" disabled={busy} className={cls.btn} data-testid="admin-blog-save"><Save size={14}/> {busy ? "Guardando..." : "Guardar"}</button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog">
      <Toaster theme="dark"/>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className={cls.h1}>Artículos del blog</h1>
          <p className={cls.sub}>Crear, editar o eliminar artículos SEO.</p>
        </div>
        <button onClick={() => setEditing({ slug: "", title: "", category: "", read_time: "5 min", hero_image: "", excerpt: "", tags: [], content: "" })} className={cls.btn} data-testid="admin-blog-new"><Plus size={14}/> Nuevo artículo</button>
      </div>
      <div className="space-y-2">
        {list.map((a) => (
          <div key={a.slug} className={`${cls.card} flex items-center justify-between gap-4`}>
            <div className="min-w-0">
              <p className="font-bold text-white truncate">{a.title}</p>
              <p className="text-xs text-white/50 truncate">{a.slug} · {a.category} · {a.content_ready === false ? "⋯ sin contenido" : "✓"}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing({ ...a, _origSlug: a.slug })} className={cls.btnGhost} data-testid={`admin-blog-edit-${a.slug}`}><Edit3 size={12}/></button>
              <button onClick={() => remove(a.slug)} className={cls.btnDanger} data-testid={`admin-blog-del-${a.slug}`}><Trash2 size={12}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== CASOS ==============
export function AdminCasos() {
  const { adminFetch } = useAdminAuth();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => adminFetch("get", "/admin/cases").then((d) => setList(d.cases));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const isNew = !editing._existing;
      if (isNew) await adminFetch("post", "/admin/cases", editing);
      else await adminFetch("put", `/admin/cases/${editing.id}`, editing);
      toast.success("Guardado"); setEditing(null); load();
    } catch { toast.error("Error"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!confirm("¿Borrar caso?")) return;
    await adminFetch("delete", `/admin/cases/${id}`); load();
  };

  if (editing) return (
    <div data-testid="admin-casos-edit">
      <Toaster theme="dark"/>
      <button onClick={() => setEditing(null)} className={`${cls.btnGhost} mb-6`}><X size={14}/> Volver</button>
      <h1 className={cls.h1}>{editing._existing ? "Editar" : "Nuevo"} caso</h1>
      <form onSubmit={save} className={`${cls.card} space-y-4 max-w-3xl`}>
        {[["clinic_name", "Clínica / Centro"], ["country", "País"], ["machine", "Equipo usado"], ["metric_headline", "Métrica destacada"], ["person", "Persona (Nombre · Cargo)"]].map(([k, l]) => (
          <div key={k}>
            <label className={cls.label}>{l}</label>
            <input className={cls.input} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}/>
          </div>
        ))}
        <MediaUpload label="Imagen" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} accept="image/*" testid="caso-image"/>
        <div>
          <label className={cls.label}>Cita / testimonio</label>
          <textarea rows={4} className={cls.input} value={editing.quote || ""} onChange={(e) => setEditing({ ...editing, quote: e.target.value })}/>
        </div>
        <button type="submit" disabled={busy} className={cls.btn}><Save size={14}/> Guardar</button>
      </form>
    </div>
  );

  return (
    <div data-testid="admin-casos">
      <Toaster theme="dark"/>
      <div className="flex justify-between items-start mb-6">
        <div><h1 className={cls.h1}>Casos de éxito</h1><p className={cls.sub}>Testimonios y métricas de clientes.</p></div>
        <button onClick={() => setEditing({ clinic_name: "", country: "", machine: "", metric_headline: "", quote: "", person: "", image: "" })} className={cls.btn}><Plus size={14}/> Nuevo caso</button>
      </div>
      <div className="space-y-2">
        {list.map((c) => (
          <div key={c.id} className={`${cls.card} flex items-center justify-between`}>
            <div><p className="font-bold text-white">{c.clinic_name}</p><p className="text-xs text-white/50">{c.metric_headline}</p></div>
            <div className="flex gap-2">
              <button onClick={() => setEditing({ ...c, _existing: true })} className={cls.btnGhost}><Edit3 size={12}/></button>
              <button onClick={() => remove(c.id)} className={cls.btnDanger}><Trash2 size={12}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== CONTACTOS ==============
export function AdminContactos() {
  const { adminFetch } = useAdminAuth();
  const [list, setList] = useState([]);

  const load = () => adminFetch("get", "/admin/contacts").then((d) => setList(d.submissions));
  useEffect(() => { load(); }, []);

  const markAttended = async (id, attended) => {
    await adminFetch("patch", `/admin/contacts/${id}`, { attended });
    load();
  };

  return (
    <div data-testid="admin-contactos">
      <Toaster theme="dark"/>
      <h1 className={cls.h1}>Mensajes de contacto</h1>
      <p className={cls.sub}>{list.length} solicitudes recibidas. Marca como atendidos los que ya hayas gestionado.</p>
      <div className="space-y-3">
        {list.length === 0 && <p className="text-white/50 text-sm">Sin mensajes todavía.</p>}
        {list.map((c) => (
          <div key={c.id} className={`${cls.card} ${c.attended ? "opacity-50" : ""}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="min-w-0 flex-1">
                <p className="font-bold text-white">{c.name} <span className="text-white/40 text-xs font-mono">· {c.sector || "—"} · {c.interest || "—"}</span></p>
                <p className="text-xs text-white/60 mt-1 flex items-center gap-3 flex-wrap">
                  <a href={`mailto:${c.email}`} className="inline-flex gap-1 items-center hover:text-[#ff9c00]"><Mail size={11}/>{c.email}</a>
                  {c.phone && <a href={`tel:${c.phone}`} className="inline-flex gap-1 items-center hover:text-[#ff9c00]"><Phone size={11}/>{c.phone}</a>}
                  {c.company && <span>· {c.company}</span>}
                </p>
                <p className="text-sm text-white/80 mt-3 whitespace-pre-wrap">{c.message}</p>
                <p className="text-xs text-white/30 mt-3 font-mono">{c.created_at?.slice(0, 19).replace("T", " ")}</p>
              </div>
              <button onClick={() => markAttended(c.id, !c.attended)} className={c.attended ? cls.btnGhost : cls.btn} data-testid={`admin-contact-mark-${c.id}`}>
                <Check size={14}/> {c.attended ? "Reabrir" : "Atendido"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
